import { JSX, useState } from 'react';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useGetIdentity, UseGetIdentityResult, useNotify, useRefresh } from 'react-admin';
import { AdminMiniDialog } from '../../admin-mini/components/AdminMiniDialog';
import {
    AdminMiniComposer,
    AdminMiniRunDraft,
    emptyDraft,
} from '../../admin-mini/components/AdminMiniComposer';
import { MYSTERY_SHOPPER } from '../../admin-mini/admin-mini.descriptors';
import { AdminMiniSendReport } from '../../admin-mini/admin-mini.types';
import { useAdminMiniAccess } from '../../admin-mini/hooks/admin-mini-access';
import { useAdminMiniActivate } from '../../admin-mini/hooks/admin-mini-requests';
import { activationPayloadFor } from '../../admin-mini/admin-mini.payload';
import { createRandomString } from '../../admin-mini/admin-mini.rules';

const TOOL = MYSTERY_SHOPPER;

/**
 * Starting a mystery shop from HQ.
 *
 * **The same composer the Compliance Sniffer uses** (user, 2026-09-11). It replaced a 1020-line
 * form of this tool's own, which had grown its own answer to every question the sniffer's composer
 * also answers - recipients, per-recipient message, sender, schedule - and had no answer at all to
 * the one that matters most: which Noks score the replies. An admin mystery shop therefore sent
 * its mail, collected the reply and produced no grades, because
 * `MysteryShopperMessageHandlerNokProcessor` reads a list the admin half never wrote.
 *
 * **The web form channel went with it** (user, 2026-09-11). That form could submit a scraped
 * contact form instead of sending email, through `MysteryShopperFormManager`, and the edit modal
 * that reached it was deleted in the same pass. The manager and its two pinned defects, HQAPI-11
 * and HQAPI-12, are untouched on the API side and nothing in HQ calls them: bringing the channel
 * back means a panel on this composer, not resurrecting the old form.
 */
export const CreateNewMysteryShopperModal: () => JSX.Element = (): JSX.Element => {
    const user: UseGetIdentityResult = useGetIdentity();
    const notify = useNotify();
    const refresh = useRefresh();
    const access = useAdminMiniAccess(TOOL);
    const { activating, activate } = useAdminMiniActivate(TOOL);

    const [open, setOpen] = useState<boolean>(false);
    const [draft, setDraft] = useState<AdminMiniRunDraft>(emptyDraft(TOOL));
    const [msCode, setMsCode] = useState<string>(createRandomString(10));
    const [report, setReport] = useState<AdminMiniSendReport | undefined>(undefined);

    function handleOpen(): void {
        setDraft(emptyDraft(TOOL));
        setMsCode(createRandomString(10));
        setReport(undefined);
        setOpen(true);
    }

    async function handleActivate(): Promise<void> {
        const answer: AdminMiniSendReport = await activate({
            ...activationPayloadFor(draft, {
                action: 'create_and_activate',
                adminUser: user?.data?.id,
            }),
            // The shop's own reference, generated in the browser and shown in the title bar so an
            // admin can quote it before the run has an id of its own.
            msCode: msCode,
            // A mystery shop has always carried this; the sniffer has one channel and sends none.
            channel: 'email',
        });

        setReport(answer);
        notify(answer.message, { type: answer.status === 'success' ? 'info' : 'warning' });

        if (answer.status === 'success') {
            refresh();
            // Held open when some recipients failed, because the report names which and closing
            // takes that away before it is read.
            if ((answer.data?.failed ?? 0) === 0) {
                setOpen(false);
            }
        }
    }

    return (
        <>
            <Button variant="contained" startIcon={ <AddIcon /> } onClick={ handleOpen }>
                { 'New mystery shop' }
            </Button>

            <AdminMiniDialog open={ open }
                             onClose={ (): void => setOpen(false) }
                             title="New mystery shop"
                             subtitle={ `Reference ${ msCode }` }>
                <AdminMiniComposer tool={ TOOL }
                                   draft={ draft }
                                   onChange={ setDraft }
                                   onActivate={ (): void => { void handleActivate(); } }
                                   activating={ activating }
                                   access={ access }
                                   report={ report } />
            </AdminMiniDialog>
        </>
    );
};
